import { THIS_SESSION_GROUP_SLUG } from "./types";

/**
 * Centralized query key constants shared across web and desktop apps.
 *
 * Using typed constants prevents typo bugs and makes it easy to refactor
 * or search for all usages of a given cache entry.
 */
export const QUERY_KEYS = {
  // ---------------------------------------------------------------------------
  // Session & Timer
  // ---------------------------------------------------------------------------
  ACTIVE_SESSION: ["activeSession"] as const,
  ACTIVE_TASK: ["activeTask"] as const,
  USER_SETTINGS: ["userSettings"] as const,

  // ---------------------------------------------------------------------------
  // Tasks
  // ---------------------------------------------------------------------------
  TASK_SERVICES: ["task-services-with-connection"] as const,

  tasks: {
    all: () => ["tasks"] as const,
    column: (column: string) => ["tasks", column] as const,
    infinite: (column: string, ...args: unknown[]) => {
      const filtered = args.filter((a) => a !== undefined && a !== "");
      return ["tasks", column, "infinite", ...filtered] as string[];
    },
    // MUST use the group slug the columns key by — a divergent literal here
    // means writers (drag, complete/uncomplete) update a cache no reader
    // subscribes to. Guarded by taskQueryKeys.parity.test.ts in apps/web.
    focus: (...args: unknown[]) =>
      ["tasks", THIS_SESSION_GROUP_SLUG, "focus", ...args] as string[],
    count: (column: string) => ["tasks", column, "count"] as string[],
  },
  externalTasks: (source: string, ...args: unknown[]) =>
    ["external-tasks", source, ...args] as const,

  // ---------------------------------------------------------------------------
  // Productivity & Points
  // ---------------------------------------------------------------------------
  USER_STATS: ["userStats"] as const,
  STARTUP_POINTS: ["startupPoints"] as const,
  BONUS_AVAILABLE: ["bonusAvailable"] as const,
  BONUS_HISTORY: ["bonusHistory"] as const,
  BONUS_TOMORROW: ["bonusTomorrow"] as const,
  AVAILABLE_SESSION_POINTS: ["availableSessionPoints"] as const,
  SESSION_FEEDBACK: ["sessionFeedback"] as const,
  /** Prefix of web's `benchmarkKeys`: "Where you stand" (TX-4793, TX-5328). */
  WEEKLY_BENCHMARK: ["productivity", "benchmark"] as const,

  // ---------------------------------------------------------------------------
  // Subscription
  // ---------------------------------------------------------------------------
  SUBSCRIPTION_STATUS: ["subscriptionStatus"] as const,
  WATCH_SUBSCRIPTION: ["watchSubscription"] as const,

  // ---------------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------------
  USER_CHANNELS: ["user-channels"] as const,
  COWORKING_USERS: ["chat-users"] as const,

  userChannels: (type: string) => ["user-channels", type] as const,
  channelDetails: (channelId: string) => ["channel", channelId] as const,
  chatMessages: (channelId: string) => ["chat-messages", channelId] as const,
  tribeChannel: () => ["tribe-channel"] as const,

  // ---------------------------------------------------------------------------
  // Music & Playlists
  // ---------------------------------------------------------------------------
  USER_PLAYLIST: ["userPlaylist"] as const,
  PLAYLISTS: ["playlists"] as const,
  PLAYLIST_ANALYTICS: ["playlist-analytics"] as const,
  PLAYLIST_BADGES: ["playlist-badges"] as const,

  playlistAnalytics: (days: number) => ["playlist-analytics", days] as const,
  playlist: (id: string) => ["playlist", id] as const,

  // ---------------------------------------------------------------------------
  // Calendar
  // ---------------------------------------------------------------------------
  CALENDAR_SETTINGS: ["settings", "calendar"] as const,
  CALENDAR_EVENTS: ["calendar-events"] as const,
  /** @deprecated Use CALENDAR_EVENTS */
  GOOGLE_CALENDAR_EVENTS: ["calendar-events"] as const,
  PUBLIC_CALENDAR: ["public-calendar"] as const,

  // ---------------------------------------------------------------------------
  // Other
  // ---------------------------------------------------------------------------
  APP_VERSION: ["appVersion"] as const,
  PERSONALITIES: ["personalities"] as const,
  ACTIVE_PERSONALITY: ["activePersonality"] as const,
  USER_DETAIL: (userId: string) => ["user-detail", userId] as const,
  COFFEE_SHOP_IMAGE: ["coffeeShopImage"] as const,

  distractions: (sessionId?: string) =>
    ["distraction-monitor", sessionId] as const,
  productivityData: (sessionId: string) =>
    ["productivityData", sessionId] as const,
  sessionMissedOpportunities: (sessionId: string) =>
    ["sessionMissedOpportunities", sessionId] as const,
  productivityLeaderboard: (limit: number, period: string) =>
    ["productivityLeaderboard", limit, period] as const,
  recentSessions: (limit: number) =>
    ["recent-sessions", limit] as const,
  paginatedRecentSessions: (page: number, limit: number) =>
    ["paginated-recent-sessions", page, limit] as const,
  streak: (userId: string) =>
    ["streak", userId] as const,
  activityStreak: (startDate: string, days: number) =>
    ["userActivityStreak", startDate, days] as const,
  sessionHistory: (days: number) =>
    ["userSessionHistory", days] as const,
} as const;
